import React, { useState } from 'react';
import { FiInstagram, FiMessageCircle, FiImage, FiCheckCircle, FiRefreshCw, FiCpu, FiInfo, FiHeart, FiCalendar } from 'react-icons/fi';
import instagramService from '../services/instagramService';
import './CommentsPage.css';

import Loading from './Loading';
const InstagramComments = ({ instagramId, accountName }) => {
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState([]);
  const [sentimentAnalysis, setSentimentAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [expandedPosts, setExpandedPosts] = useState({});
  const [selectedSentiment, setSelectedSentiment] = useState(null); // null, 'positive', 'neutral', 'negative'

  // Date range filter
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);

  const fetchComments = async (analyzeEnabled = false) => {
    setLoading(true);
    setError(null);

    try {
      const response = await instagramService.getComments(
        instagramId,
        25,  // limit_posts
        50,  // limit_comments_per_post
        analyzeEnabled,
        dateFrom,  // date_from filter
        dateTo     // date_to filter
      );

      setStats({
        total_comments: response.data.total_comments,
        posts_analyzed: response.data.posts_analyzed,
        comments_saved: response.data.comments_saved
      });

      setComments(response.data.comments_by_post || {});

      if (response.data.sentiment_analysis) {
        setSentimentAnalysis(response.data.sentiment_analysis);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error al obtener comentarios');
      console.error('Error fetching Instagram comments:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !stats) {
    return (
      <div className="comments-page">
        <Loading fullScreen={false} size="sm" mensaje="Cargando comentarios de Instagram" />
      </div>
    );
  }

  return (
    <div className="comments-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-left">
            <div className="header-icon">
              <FiInstagram size={32} />
            </div>
            <div>
              <h1>Comentarios de Instagram</h1>
              <p className="subtitle">{accountName}</p>
            </div>
          </div>

          <div className="header-actions">
            <button
              onClick={() => fetchComments(false)}
              disabled={loading}
              className="btn btn-primary"
            >
              <FiRefreshCw className={loading ? 'spinning' : ''} />
              {loading ? 'Cargando...' : 'Obtener Comentarios'}
            </button>

            <button
              onClick={() => fetchComments(true)}
              disabled={loading}
              className="btn btn-primary"
            >
              <FiCpu className={loading ? 'spinning' : ''} />
              {loading ? 'Analizando...' : 'Obtener y Analizar IA'}
            </button>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert-error">
          <div className="alert-error-content">
            <FiInfo size={20} className="alert-error-icon" />
            <p className="alert-error-text">{error}</p>
          </div>
        </div>
      )}

      {/* Date Filter Section */}
      <div className="date-filter-section">
        <button
          onClick={() => setShowDateFilter(!showDateFilter)}
          className="btn btn-primary"
          style={{ marginBottom: '1rem' }}
        >
          <FiCalendar size={18} />
          {showDateFilter ? 'Ocultar Filtro de Fechas' : 'Filtrar por Fechas'}
        </button>

        {showDateFilter && (
          <div className="date-filter-container">
            <div className="date-filter-inputs">
              <div className="date-input-group">
                <label htmlFor="date-from">Desde:</label>
                <input
                  id="date-from"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="date-input"
                />
              </div>

              <div className="date-input-group">
                <label htmlFor="date-to">Hasta:</label>
                <input
                  id="date-to"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="date-input"
                />
              </div>

              <button
                onClick={() => {
                  setDateFrom('');
                  setDateTo('');
                }}
                className="btn btn-secondary"
                style={{ alignSelf: 'flex-end' }}
              >
                Limpiar
              </button>
            </div>

            {(dateFrom || dateTo) && (
              <div className="date-filter-info">
                <FiInfo size={16} />
                <span>
                  {dateFrom && dateTo
                    ? `Mostrando comentarios desde ${dateFrom} hasta ${dateTo}`
                    : dateFrom
                    ? `Mostrando comentarios desde ${dateFrom}`
                    : `Mostrando comentarios hasta ${dateTo}`}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Stats Grid */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <FiMessageCircle />
            </div>
            <div className="stat-content">
              <div className="stat-label">Total Comentarios</div>
              <div className="stat-value">{stats.total_comments}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <FiImage />
            </div>
            <div className="stat-content">
              <div className="stat-label">Posts Analizados</div>
              <div className="stat-value">{stats.posts_analyzed}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <FiCheckCircle />
            </div>
            <div className="stat-content">
              <div className="stat-label">Comentarios Guardados</div>
              <div className="stat-value">{stats.comments_saved}</div>
            </div>
          </div>
        </div>
      )}

      {/* Sentiment Analysis */}
      {sentimentAnalysis && (
        <div className="sentiment-section">
          <div className="sentiment-header">
            <FiCpu size={24} style={{ color: '#10b981' }} />
            <h3>Análisis de Sentimientos con IA</h3>
          </div>

          <div className="sentiment-grid">
            <div
              className={`sentiment-card ${selectedSentiment === 'positive' ? 'active' : ''}`}
              onClick={() => setSelectedSentiment(selectedSentiment === 'positive' ? null : 'positive')}
              style={{ cursor: 'pointer' }}
            >
              <div className="sentiment-emoji">😊</div>
              <div className="sentiment-label">Positivos</div>
              <div className="sentiment-value positive">
                {sentimentAnalysis.sentiment_distribution?.positive || 0}
              </div>
              <div className="sentiment-percentage">
                {stats?.total_comments > 0
                  ? `${Math.round((sentimentAnalysis.sentiment_distribution?.positive || 0) / stats.total_comments * 100)}%`
                  : '0%'
                }
              </div>
            </div>

            <div
              className={`sentiment-card neutral ${selectedSentiment === 'neutral' ? 'active' : ''}`}
              onClick={() => setSelectedSentiment(selectedSentiment === 'neutral' ? null : 'neutral')}
              style={{ cursor: 'pointer' }}
            >
              <div className="sentiment-emoji">😐</div>
              <div className="sentiment-label">Neutrales</div>
              <div className="sentiment-value neutral">
                {sentimentAnalysis.sentiment_distribution?.neutral || 0}
              </div>
              <div className="sentiment-percentage">
                {stats?.total_comments > 0
                  ? `${Math.round((sentimentAnalysis.sentiment_distribution?.neutral || 0) / stats.total_comments * 100)}%`
                  : '0%'
                }
              </div>
            </div>

            <div
              className={`sentiment-card negative ${selectedSentiment === 'negative' ? 'active' : ''}`}
              onClick={() => setSelectedSentiment(selectedSentiment === 'negative' ? null : 'negative')}
              style={{ cursor: 'pointer' }}
            >
              <div className="sentiment-emoji">😞</div>
              <div className="sentiment-label">Negativos</div>
              <div className="sentiment-value negative">
                {sentimentAnalysis.sentiment_distribution?.negative || 0}
              </div>
              <div className="sentiment-percentage">
                {stats?.total_comments > 0
                  ? `${Math.round((sentimentAnalysis.sentiment_distribution?.negative || 0) / stats.total_comments * 100)}%`
                  : '0%'
                }
              </div>
            </div>
          </div>

          {sentimentAnalysis.insights && (
            <div className="insights-box">
              <div className="insights-header">
                <FiInfo size={18} />
                <h4>Insights de OpenAI:</h4>
              </div>
              <p className="insights-text">{sentimentAnalysis.insights}</p>
            </div>
          )}
        </div>
      )}

      {/* Comments by Post - Solo se muestra cuando hay un sentiment seleccionado */}
      {selectedSentiment && Object.keys(comments).length > 0 && (
        <div className="comments-section">
          <h3 className="section-title">
            <FiMessageCircle size={28} style={{ color: '#10b981' }} />
            Comentarios {selectedSentiment === 'positive' ? 'Positivos' : selectedSentiment === 'neutral' ? 'Neutrales' : 'Negativos'}
          </h3>

          {Object.entries(comments)
            .filter(([postId, postData]) => {
              // Filtrar posts que tengan comentarios del sentiment seleccionado
              if (!postData.comments || postData.comments.length === 0) return false;
              return postData.comments.some(comment => comment.sentiment_label === selectedSentiment);
            })
            .map(([postId, postData]) => {
              const isExpanded = expandedPosts[postId];
              const toggleExpand = () => {
                setExpandedPosts(prev => ({
                  ...prev,
                  [postId]: !prev[postId]
                }));
              };

              // Filtrar comentarios del sentiment seleccionado
              const filteredComments = postData.comments.filter(
                comment => comment.sentiment_label === selectedSentiment
              );

              if (filteredComments.length === 0) return null;

              return (
                <div key={postId} className="post-card">
                  <div className="post-header">
                    <div className="post-info">
                      <h4>Post de Instagram</h4>
                      {postData.post_info.caption && (
                        <p className="post-caption">{postData.post_info.caption}</p>
                      )}
                    </div>
                    {postData.post_info.permalink && (
                      <a
                        href={postData.post_info.permalink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="post-link"
                      >
                        <FiInstagram size={16} />
                        Ver en Instagram
                      </a>
                    )}
                  </div>

                  <button
                    onClick={toggleExpand}
                    className="toggle-comments-btn"
                  >
                    <FiMessageCircle size={18} />
                    <span>
                      {isExpanded ? 'Ocultar' : 'Ver'} {filteredComments.length} comentario{filteredComments.length !== 1 ? 's' : ''}
                    </span>
                    <span className="toggle-icon">{isExpanded ? '▲' : '▼'}</span>
                  </button>

                  {isExpanded && (
                    <div className="comments-list">
                      {filteredComments.map((comment, idx) => (
                        <div key={idx} className="comment-item">
                          <div className="comment-content">
                            <div className="comment-text-wrapper">
                              <span className="comment-username">
                                {comment.username ? `@${comment.username}` : '👤 Usuario'}
                              </span>
                              <p className="comment-text">{comment.text}</p>
                            </div>
                            <div className="comment-meta">
                              <span
                                className={`sentiment-badge ${comment.sentiment_label}`}
                                style={{
                                  fontSize: '0.75rem',
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  fontWeight: '500'
                                }}
                              >
                                {comment.sentiment_label === 'positive' ? '😊 Positivo' :
                                 comment.sentiment_label === 'neutral' ? '😐 Neutral' :
                                 '😞 Negativo'}
                              </span>
                              <div className="comment-likes">
                                <FiHeart size={16} />
                                {comment.like_count}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}

      {/* Empty State */}
      {Object.keys(comments).length === 0 && !loading && !error && (
        <div className="empty-state">
          <FiMessageCircle size={48} />
          <p>No hay comentarios disponibles</p>
          <p style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '0.5rem' }}>
            Haz clic en "Obtener Comentarios" para cargar los comentarios de tus posts.
          </p>
        </div>
      )}
    </div>
  );
};

export default InstagramComments;
